import { useLazyQuery } from "@apollo/client";
import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";

import {
    GetAssetIntensityChartDataQuery,
    GetAssetIntensityChartDataQueryVariables,
    CrremCategory,
    InsightIntensityChartData,
    CrremPathway,
} from "graphql-types/graphql";
import { useEpcSummaryQuery } from "legacyNext/hooks";
import { GET_ASSET_INTENSITY_CHAT_DATA } from "legacyNext/hooks/useAssetIntensity/use-asset-intensity.query";
import { ReductionProposal } from "legacyNext/types";

type StoreContext = {
    loading: boolean;
    proposals: Array<ReductionProposal>;
    setProposals: (proposals: Array<ReductionProposal>) => void;
    fetchIntensity: () => void;
    pathways: Omit<CrremPathway, "country" | "target">[];
    intensities: InsightIntensityChartData[];
};

const Store = createContext<StoreContext | undefined>(undefined);

type Props = {
    assetId: string;
    assetBfe: string;
    crremCategories: CrremCategory[];
};

export const AssetStoreProvider = (props: PropsWithChildren<Props>) => {
    const { assetId, assetBfe, crremCategories, children } = props;
    const [proposals, setProposals] = useState<ReductionProposal[]>([]);

    const { data: epcSummary, loading: epcSummaryLoading } =
        useEpcSummaryQuery(assetBfe);

    const [fetchIntensity, { data, loading }] = useLazyQuery<
        GetAssetIntensityChartDataQuery,
        GetAssetIntensityChartDataQueryVariables
    >(GET_ASSET_INTENSITY_CHAT_DATA, {
        variables: {
            assetId,
            categories: crremCategories.filter(
                (c) => c !== CrremCategory.UNCATEGORIZED
            ),
        },
    });

    const fetchIntensityData = () => fetchIntensity();

    useEffect(() => {
        if (epcSummaryLoading || proposals.length > 0) {
            return;
        }

        let index = 1;
        const fetchedProposals = epcSummary.flatMap(
            (e) =>
                e.proposals?.map((p) => ({
                    ...p,
                    id: (index++).toString(),
                    epc: e.identifier,
                    address: e.street ?? "",
                    enabled: false,
                    startYear: null,
                    labels: {
                        current: e.classification ?? "",
                        profitable: e.profitableProposalClassification ?? "",
                        possible: e.allProposalClassification ?? "",
                    },
                })) ?? []
        );
        setProposals(fetchedProposals);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [epcSummaryLoading]);

    return (
        <Store.Provider
            value={{
                proposals,
                setProposals,
                loading: epcSummaryLoading || loading,
                fetchIntensity: fetchIntensityData,
                pathways: data?.getCrremPathways ?? [],
                intensities: data?.getIntensityChartData ?? [],
            }}
        >
            {children}
        </Store.Provider>
    );
};

export const useAssetStore = () => {
    const context = useContext(Store);

    if (!context)
        throw new Error(
            "useAssetStore must be used within a AssetStoreProvider"
        );

    return context;
};
